import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import MoreButtonWrapper from '../Common/MoreButtonWrapper'
import CopyLinkModalItem from '../../MorePopupsOfEditModals/Common/CopyLinkModalItem'
import ModalItem from '../../MorePopupsOfEditModals/Common/ModalItem'
import OpenInNewWindowModalItem from '../Common/OpenInNewWindowModalItem'
import OpenProjectModalItem from '../Common/OpenProjectModalItem'
import SyncCalendarModalItem from './SyncCalendarModalItem'
import DateBarOrganizeModalItem from './DateBarOrganizeModalItem'
import { checkIfSelectedAllProjects } from '../../../../SettingsView/ProjectsSettings/ProjectHelper'
import OKRModal from '../../../../TaskListView/OKRs/OKRModal'
import AutoPostponeTasksModal from '../../../../TaskListView/AutoPostpone/AutoPostponeTasksModal'
import { DATE_TASK_INDEX, TODAY_DATE } from '../../../../../utils/backends/openTasks'
import { areEmailLineConnectionsHiddenToday } from '../../../../TaskListView/EmailLine/emailLineHelper'
import { EMAIL_LINE_ENABLED } from '../../../../TaskListView/EmailLine/emailLineFeature'
import { listEmailConnections } from '../../../../../utils/IntegrationProviders'
import {
    clearUserEmailLineHiddenTodayForConnections,
    clearUserOKRsHiddenInAllProjectsToday,
} from '../../../../../utils/backends/Users/usersFirestore'
import { getOkrAllProjectsTodayKey, getOkrUserTimezone } from '../../../../TaskListView/OKRs/okrHelper'
import ProjectHelper from '../../../../SettingsView/ProjectsSettings/ProjectHelper'
import NavigationService from '../../../../../utils/NavigationService'
import { DV_TAB_PROJECT_OKRS } from '../../../../../utils/TabNavigationConstants'
import PrioritizeTasksModalItem from './PrioritizeTasksModalItem'
import { openProjectWiki } from '../../../../WikiView/wikiLinks'
import RunOutOfGoldAssistantModal from '../../../../ChatsView/ChatDV/EditorView/BotOption/RunOutOfGoldAssistantModal'

export default function TaskHeaderMoreButton({
    projectIdOverride,
    userId,
    wrapperStyle,
    buttonStyle,
    disabled,
    shortcut = 'M',
    iconSize,
    iconColor,
}) {
    const selectedProjectId = useSelector(state => {
        const { selectedProjectIndex, loggedUserProjects } = state

        return checkIfSelectedAllProjects(selectedProjectIndex)
            ? null
            : loggedUserProjects[selectedProjectIndex]
              ? loggedUserProjects[selectedProjectIndex].id
              : null
    })
    const projectId = projectIdOverride !== undefined ? projectIdOverride : selectedProjectId
    const projectOKRs = useSelector(state => (projectId ? state.okrsByProjectInTasks[projectId] || [] : []))
    // Organize / Select all act on the "Today" section of this project's task list. The date-section
    // store is keyed by `projectId + userId` and each section carries its own date string, so we find
    // the index of the section whose date is TODAY_DATE. -1 means the list isn't loaded yet / no today
    // section, in which case we don't offer the items.
    const instanceKey = projectId && userId ? `${projectId}${userId}` : null
    const todayDateIndex = useSelector(state => {
        if (!instanceKey) return -1
        const dateSections = state.filteredOpenTasksStore[instanceKey]
        if (!dateSections) return -1
        return dateSections.findIndex(section => section[DATE_TASK_INDEX] === TODAY_DATE)
    })
    const loggedUser = useSelector(state => state.loggedUser)
    const [showAddOKR, setShowAddOKR] = useState(false)
    const [showAutoPostpone, setShowAutoPostpone] = useState(false)
    const [showRunOutOfGold, setShowRunOutOfGold] = useState(false)
    const modalRef = useRef()
    const openAddOKRTimeoutRef = useRef()
    const openAutoPostponeTimeoutRef = useRef()

    const inSelectedProject = !!projectId
    const todayKey = getOkrAllProjectsTodayKey(undefined, getOkrUserTimezone(loggedUser))
    const okrsHiddenTodayById = loggedUser.okrsHiddenInAllProjectsTodayByProjectAndOkr?.[projectId] || {}
    const okrsHiddenToday = projectOKRs.filter(okr => okrsHiddenTodayById[okr.id] === todayKey)
    const allProjectOKRsDoneToday = projectOKRs.length > 0 && okrsHiddenToday.length === projectOKRs.length
    const showAddOKRItem = !!projectId && (projectOKRs.length === 0 || allProjectOKRsDoneToday)
    const showOrganizeItems = inSelectedProject && todayDateIndex >= 0

    // "Done for today" on the Email line hides it completely; this menu item is the
    // way to bring it back before the daily reset.
    const emailConnectionIds = listEmailConnections(loggedUser).map(connection => connection.connectionId)
    const showEmailLineItem =
        EMAIL_LINE_ENABLED && !inSelectedProject && areEmailLineConnectionsHiddenToday(loggedUser, emailConnectionIds)

    const link = inSelectedProject
        ? `${window.location.origin}/projects/${projectId}/user/${userId}/tasks/open`
        : `${window.location.origin}/projects/tasks/open`

    const dismissModal = () => {
        modalRef?.current?.close()
    }

    const clearOpenAddOKRTimeout = () => {
        if (openAddOKRTimeoutRef.current) {
            clearTimeout(openAddOKRTimeoutRef.current)
            openAddOKRTimeoutRef.current = null
        }
    }

    const openAddOKR = e => {
        e?.preventDefault?.()
        e?.stopPropagation?.()
        e?.nativeEvent?.stopImmediatePropagation?.()

        clearOpenAddOKRTimeout()
        openAddOKRTimeoutRef.current = setTimeout(() => {
            openAddOKRTimeoutRef.current = null
            setShowAddOKR(true)
        })
    }

    const closeAddOKR = () => {
        clearOpenAddOKRTimeout()
        setShowAddOKR(false)
        dismissModal()
    }

    const clearOpenAutoPostponeTimeout = () => {
        if (openAutoPostponeTimeoutRef.current) {
            clearTimeout(openAutoPostponeTimeoutRef.current)
            openAutoPostponeTimeoutRef.current = null
        }
    }

    const openAutoPostpone = e => {
        e?.preventDefault?.()
        e?.stopPropagation?.()
        e?.nativeEvent?.stopImmediatePropagation?.()

        clearOpenAutoPostponeTimeout()
        openAutoPostponeTimeoutRef.current = setTimeout(() => {
            openAutoPostponeTimeoutRef.current = null
            setShowAutoPostpone(true)
        })
    }

    const closeAutoPostpone = () => {
        clearOpenAutoPostponeTimeout()
        setShowAutoPostpone(false)
        dismissModal()
    }

    const openOKRsTab = () => {
        ProjectHelper.processURLProjectDetailsTab(NavigationService, DV_TAB_PROJECT_OKRS, projectId)
        dismissModal()
    }

    const undoAllOKRsForToday = () => {
        clearUserOKRsHiddenInAllProjectsToday(
            loggedUser.uid,
            projectId,
            okrsHiddenToday.map(okr => okr.id)
        )
        dismissModal()
    }

    const onCloseMainModal = () => {
        clearOpenAddOKRTimeout()
        clearOpenAutoPostponeTimeout()
        setShowAddOKR(false)
        setShowAutoPostpone(false)
        setShowRunOutOfGold(false)
    }

    useEffect(() => {
        return () => {
            clearOpenAddOKRTimeout()
            clearOpenAutoPostponeTimeout()
        }
    }, [])

    const renderItems = () => {
        const list = []

        list.push(shortcut => (
            <PrioritizeTasksModalItem
                key="gmbtn-prioritize"
                projectId={projectId}
                shortcut={shortcut}
                onPress={dismissModal}
                onRunOutOfGold={() => setShowRunOutOfGold(true)}
            />
        ))

        if (showOrganizeItems) {
            list.push(shortcut => {
                return (
                    <DateBarOrganizeModalItem
                        key={'gmbtn-organize'}
                        icon={'multi-selection'}
                        text={'Organize'}
                        shortcut={shortcut}
                        onPress={dismissModal}
                        projectId={projectId}
                        dateIndex={todayDateIndex}
                        instanceKey={instanceKey}
                    />
                )
            })

            list.push(shortcut => {
                return (
                    <DateBarOrganizeModalItem
                        key={'gmbtn-select-all'}
                        icon={'multi-selection-selected'}
                        text={'Select all'}
                        shortcut={shortcut}
                        onPress={dismissModal}
                        projectId={projectId}
                        dateIndex={todayDateIndex}
                        instanceKey={instanceKey}
                        selectTasks={true}
                    />
                )
            })
        }

        list.push(shortcut => {
            return (
                <ModalItem
                    key={'gmbtn-auto-postpone'}
                    icon={'coffee'}
                    text={'Auto-postpone tasks'}
                    shortcut={shortcut}
                    onPress={openAutoPostpone}
                />
            )
        })

        if (allProjectOKRsDoneToday) {
            list.push(shortcut => {
                return (
                    <ModalItem
                        key={'gmbtn-okr-history'}
                        icon={'external-link'}
                        text={'OKR History'}
                        shortcut={shortcut}
                        onPress={openOKRsTab}
                    />
                )
            })

            list.push(shortcut => {
                return (
                    <ModalItem
                        key={'gmbtn-undo-okrs-today'}
                        icon={'rotate-ccw'}
                        text={'Undo all OKRs for today'}
                        shortcut={shortcut}
                        onPress={undoAllOKRsForToday}
                    />
                )
            })
        }

        if (showAddOKRItem) {
            list.push(shortcut => {
                return (
                    <ModalItem
                        key={'gmbtn-add-okr'}
                        icon={'plus-square'}
                        text={'Add OKR'}
                        shortcut={shortcut}
                        onPress={openAddOKR}
                    />
                )
            })
        }

        // The wiki is for members only (SharedHelper.isMember); a guest of a shared project has no way in.
        if (inSelectedProject && !loggedUser.isAnonymous && (loggedUser.projectIds || []).includes(projectId)) {
            list.push(shortcut => {
                return (
                    <ModalItem
                        key={'gmbtn-project-wiki'}
                        icon={'book-open'}
                        text={'Project wiki'}
                        shortcut={shortcut}
                        onPress={() => {
                            dismissModal()
                            openProjectWiki(projectId)
                        }}
                    />
                )
            })
        }

        list.push(shortcut => {
            return <CopyLinkModalItem key={'gmbtn-copy-link'} link={link} shortcut={shortcut} onPress={dismissModal} />
        })

        list.push(shortcut => {
            return <OpenInNewWindowModalItem key={'gmbtn-open-new-win'} shortcut={shortcut} onPress={dismissModal} />
        })

        if (!inSelectedProject) {
            list.push(shortcut => {
                return <SyncCalendarModalItem key={'gmbtn-sync-calendar'} shortcut={shortcut} onPress={dismissModal} />
            })
        }

        if (showEmailLineItem) {
            list.push(shortcut => {
                return (
                    <ModalItem
                        key={'gmbtn-show-email-line'}
                        icon={'mail'}
                        text={'Show email line'}
                        shortcut={shortcut}
                        onPress={() => {
                            clearUserEmailLineHiddenTodayForConnections(loggedUser.uid, emailConnectionIds)
                            dismissModal()
                        }}
                    />
                )
            })
        }

        return list
    }

    return (
        <MoreButtonWrapper
            ref={modalRef}
            buttonStyle={buttonStyle}
            disabled={disabled}
            shortcut={shortcut}
            wrapperStyle={wrapperStyle}
            iconSize={iconSize}
            iconColor={iconColor}
            onCloseModal={onCloseMainModal}
            customModal={
                showRunOutOfGold ? (
                    <RunOutOfGoldAssistantModal
                        closeModal={dismissModal}
                        closeModalWhenNavigateToPremium={dismissModal}
                    />
                ) : showAddOKR ? (
                    <OKRModal projectId={projectId} closePopover={closeAddOKR} />
                ) : showAutoPostpone ? (
                    <AutoPostponeTasksModal projectId={projectId} closePopover={closeAutoPostpone} />
                ) : null
            }
        >
            {inSelectedProject && <OpenProjectModalItem projectId={projectId} shortcut={'1'} onPress={dismissModal} />}
            {renderItems().map((item, index) => item((index + (inSelectedProject ? 2 : 1)).toString()))}
        </MoreButtonWrapper>
    )
}
